import { useForm } from '@mantine/form';
import { Box, Checkbox, ComboboxItem, Divider, Flex, Group, Modal, Stack, Text } from '@mantine/core';
import { BNTextInput } from '../../../components/TextInput/TextInput';
import { BNNumberInput } from '../../../components/NumberInput/NumberInput';
import { BNButton } from '../../../components/Button/Button';
import AutoPriceIcon from '../../../components/icons/AutoPrice';
import { formatCurrency, formatDate } from '../../../utils/formatters';
import { DateFormats } from '../../../utils/globals';
import classes from './SellPrivately.styles.module.css';
import { BNSelect } from '../../../components/Select/Select';
import PointOfSaleIcon from '../../../components/icons/PointOfSale';
import { useAtomValue } from 'jotai';
import { selectedMergedListingAtom } from '../../../data/atoms';
import { TicketStockDisplay } from '../TicketStock/TicketStockDisplay';
import { BrokerNerdsBarkerCoreModelsSalesCreateSaleVendorPropertiesVictoryLive, usePostApiSales } from '../../../api';

export function SellPrivatelyDialog({ opened, onClose }: { opened: boolean; onClose: () => void }) {
  const selectedListing = useAtomValue(selectedMergedListingAtom);

  const { mutateAsync: sellPrivately, isPending: isSellingPrivately } = usePostApiSales({
    axios: {
      headers: {
        'x-tenant-id': selectedListing?.tenantId,
      },
    },
  });

  const mobileForm = useForm({
    initialValues: {
      keepCost: false,
      totalPrice: 0,
      email: '',
      firstName: '',
      lastName: '',
    },
    validate: {
      email: (value) => (!value.includes('@') ? 'Email is invalid' : undefined),
    },
  });

  const hardForm = useForm({
    initialValues: {
      keepCost: false,
      totalPrice: 0,
      firstName: '',
      lastName: '',
      address1: '',
      address2: '',
      city: '',
      region: '',
      postalCode: '',
      countryCode: '',
    },
    validate: {
      firstName: (value) => (value.length < 1 ? 'First name is required' : undefined),
      lastName: (value) => (value.length < 1 ? 'Last name is required' : undefined),
      address1: (value) => (value.length < 1 ? 'Address is required' : undefined),
      city: (value) => (value.length < 1 ? 'City is required' : undefined),
      region: (value) => (value.length < 1 ? 'Region is required' : undefined),
      postalCode: (value) => (value.length < 1 ? 'Postal code is required' : undefined),
      countryCode: (value) => (!value ? 'Country is required' : undefined),
    },
  });

  const pdfForm = useForm({
    initialValues: {
      keepCost: false,
      totalPrice: 0,
    },
  });

  const countries = [
    {
      code: 'CAN',
      label: 'Canada',
    },
    {
      code: 'GBR',
      label: 'United Kingdom',
    },
    {
      code: 'USA',
      label: 'United States',
    },
  ];

  function EventTitle() {
    if (!selectedListing) {
      return null;
    }

    return (
      <Group mt={-2} pos="relative" style={{ zIndex: 1 }}>
        <Box>
          <Text size="xs" fw={600}>
            {selectedListing.event.name}
          </Text>
          <Text p={0} c="var(--colors-gray-5)" size="xs" fw={500}>
            <span>
              <span>{formatDate(selectedListing.event.localDateTime, DateFormats.Extended)}</span> - {selectedListing.event.venue.name} · {selectedListing.event.venue.city},{' '}
              {selectedListing.event.venue.state}
            </span>
          </Text>
        </Box>
      </Group>
    );
  }

  if (!selectedListing) {
    return null;
  }

  const { ticketStockId } = selectedListing;

  return (
    <Modal
      centered
      size={640}
      opened={opened}
      closeButtonProps={{ size: 'md' }}
      onClose={onClose}
      title={
        <Group>
          <PointOfSaleIcon color="var(--colors-iconFill)" />
          Sell Privately
        </Group>
      }
    >
      <EventTitle />
      <Group mx="-1rem" px="md" pt="sm" pb="md" mb="sm" className={classes.gradient}>
        <Group align="start" gap={0} justify="space-between" style={{ border: '1px solid var(--colors-gray-3)', borderRadius: 3 }} p="sm" w="100%">
          <Box maw="20%" style={{ overflow: 'hidden' }}>
            <Text style={{ whiteSpace: 'nowrap', lineHeight: 1 }} size="xs" c="var(--colors-gray-5)">
              Section
            </Text>
            <Text lh={1.25} pt={2} size="xs" style={{ whiteSpaceCollapse: 'collapse', overflowWrap: 'break-word' }}>
              {selectedListing.section}
            </Text>
          </Box>
          <Box maw="20%" style={{ overflow: 'hidden' }}>
            <Text style={{ whiteSpace: 'nowrap', lineHeight: 1 }} size="xs" c="var(--colors-gray-5)">
              Row
            </Text>
            <Text lh={1.25} pt={2} size="xs" style={{ whiteSpaceCollapse: 'collapse', overflowWrap: 'break-word' }}>
              {selectedListing.row}
            </Text>
          </Box>
          <Box>
            <Text style={{ whiteSpace: 'nowrap', lineHeight: 1 }} size="xs" c="var(--colors-gray-5)">
              Seats
            </Text>
            <Text lh={1.25} pt={2} size="xs">
              {(selectedListing.quantityRemaining ?? 0 > 1) ? `${selectedListing.seatFrom}-${selectedListing.seatThru}` : selectedListing.seatFrom}
            </Text>
          </Box>
          <Box>
            <Text style={{ whiteSpace: 'nowrap', lineHeight: 1 }} size="xs" c="var(--colors-gray-5)">
              Qty
            </Text>
            <Text lh={1.25} pt={2} size="xs">
              {selectedListing.quantityRemaining}
            </Text>
          </Box>
          <Box>
            <Text style={{ whiteSpace: 'nowrap', lineHeight: 1 }} size="xs" c="var(--colors-gray-5)">
              Unit Cost
            </Text>
            <Text lh={1.25} pt={2} size="xs">
              {formatCurrency(selectedListing.unitCost ?? 0)}
            </Text>
          </Box>
          <Box>
            <Text style={{ whiteSpace: 'nowrap', lineHeight: 1 }} size="xs" c="var(--colors-gray-5)">
              Current Price
            </Text>
            <Group gap={4}>
              <Text size="xs">{formatCurrency(selectedListing.unitPrice ?? 0)}</Text>
              {selectedListing?.pricerStatusId === 'AutoPriced' && <AutoPriceIcon size={16} />}{' '}
              {/* TODO: Replace this with the logic for auto pricing indicators to be developed later */}
            </Group>
          </Box>
          <Box pr={0} w={108}>
            <Text style={{ whiteSpace: 'nowrap', lineHeight: 1 }} size="xs" c="var(--colors-gray-5)">
              Stock
            </Text>
            <TicketStockDisplay ticketStockId={ticketStockId} />
          </Box>
        </Group>
      </Group>
      {(ticketStockId === 'TicketmasterMobile' || ticketStockId === 'AxsMobile' || ticketStockId === 'Qr') && (
        <form
          onSubmit={mobileForm.onSubmit(async (values) => {
            await sellPrivately({
              data: {
                currencyCode: 'USD',
                totalAmount: values.totalPrice,
                customerId: '-1', // -1 implies Sell Privately and will be handled properly by the backend
                listingIds: [selectedListing.listingId],
                vendorProperties: {
                  pointOfSaleId: 'VictoryLiveOne',
                  keepCost: values.keepCost,
                  contact: {
                    contactTypeId: 'Broker',
                    displayName: `${values.firstName} ${values.lastName}`,
                    firstName: values.firstName,
                    lastName: values.lastName,
                    email: values.email,
                  },
                } as BrokerNerdsBarkerCoreModelsSalesCreateSaleVendorPropertiesVictoryLive,
              },
            });
            onClose();
          })}
        >
          <Stack gap={6}>
            <Group gap="lg" pb={6}>
              <BNNumberInput
                size="lg"
                decimalScale={2}
                fixedDecimalScale
                selectOnFocus
                leftSection={
                  <Text size="lg" ml={-2}>
                    $
                  </Text>
                }
                {...mobileForm.getInputProps('totalPrice')}
                label="Total Price"
                labelProps={{ fz: 'xs' }}
                w={168}
              />
              <Box pt="lg">
                <Checkbox size="sm" {...mobileForm.getInputProps('keepCost')} label={<Text size="xs">Keep Cost</Text>} />
              </Box>
            </Group>
            <Divider orientation="horizontal" color="var(--colors-borderDivider)" my="xs" />
            <Group gap="md" pb={4} mt={-4}>
              <BNTextInput w={140} size="xs" {...mobileForm.getInputProps('firstName')} maxLength={50} label="First Name" />
              <BNTextInput w={140} size="xs" {...mobileForm.getInputProps('lastName')} maxLength={50} label="Last Name" />
              <BNTextInput w={296} size="xs" {...mobileForm.getInputProps('email')} maxLength={100} label="Email" />
            </Group>
            <Divider orientation="horizontal" color="var(--colors-divider)" my="xs" mx={-16} />
            <Flex justify="flex-end" gap={12}>
              <BNButton size="xs" w={80} onClick={onClose} disabled={isSellingPrivately}>
                Cancel
              </BNButton>
              <BNButton type="submit" size="xs" variant="filled" color="green" w={80} loading={isSellingPrivately}>
                Save
              </BNButton>
            </Flex>
          </Stack>
        </form>
      )}
      {ticketStockId === 'Hard' && (
        <form
          onSubmit={hardForm.onSubmit(async (values) => {
            await sellPrivately({
              data: {
                currencyCode: 'USD',
                totalAmount: values.totalPrice,
                customerId: '-1', // -1 implies Sell Privately and will be handled properly by the backend
                listingIds: [selectedListing.listingId],
                vendorProperties: {
                  pointOfSaleId: 'VictoryLiveOne',
                  keepCost: values.keepCost,
                  contact: {
                    contactTypeId: 'Broker',
                    displayName: `${values.firstName} ${values.lastName}`,
                    firstName: values.firstName,
                    lastName: values.lastName,
                    address1: values.address1,
                    address2: values.address2,
                    city: values.city,
                    state: values.region,
                    country: values.countryCode,
                    postalCode: values.postalCode,
                  },
                } as BrokerNerdsBarkerCoreModelsSalesCreateSaleVendorPropertiesVictoryLive,
              },
            });
            onClose();
          })}
        >
          <Stack gap={6}>
            <Group gap="lg" pb={6}>
              <BNNumberInput
                size="lg"
                decimalScale={2}
                fixedDecimalScale
                selectOnFocus
                leftSection={
                  <Text size="lg" ml={-2}>
                    $
                  </Text>
                }
                {...hardForm.getInputProps('totalPrice')}
                label="Total Price"
                labelProps={{ fz: 'xs' }}
                w={168}
              />
              <Box pt="lg">
                <Checkbox size="xs" {...hardForm.getInputProps('keepCost')} label="Keep Cost" />
              </Box>
            </Group>
            <Divider orientation="horizontal" color="var(--colors-borderDivider)" my="xs" />
            <Group gap="md" mt={-4}>
              <BNTextInput w={140} size="xs" {...hardForm.getInputProps('firstName')} maxLength={50} label="First Name" />
              <BNTextInput w={140} size="xs" {...hardForm.getInputProps('lastName')} maxLength={50} label="Last Name" />
            </Group>
            <Group gap="md">
              <BNTextInput w={296} size="xs" {...hardForm.getInputProps('address1')} maxLength={50} label="Address" />
              <BNTextInput w={296} size="xs" {...hardForm.getInputProps('address2')} maxLength={50} label="Address 2" />
            </Group>
            <Group gap="md" pb={4}>
              <BNTextInput w={140} size="xs" {...hardForm.getInputProps('city')} maxLength={50} label="City" />
              <BNTextInput w={140} size="xs" {...hardForm.getInputProps('region')} maxLength={50} label="State/Region" />
              <BNTextInput w={140} size="xs" {...hardForm.getInputProps('postalCode')} maxLength={15} label="Postal Code" />
              <BNSelect
                size="xs"
                w={140}
                data={countries.map(
                  (c) =>
                    ({
                      value: c.code.toString(),
                      label: c.label,
                    }) satisfies ComboboxItem,
                )}
                {...hardForm.getInputProps('country')}
                label="Country"
              />
            </Group>
            <Divider orientation="horizontal" color="var(--colors-divider)" my="xs" mx={-16} />
            <Flex justify="flex-end" gap={12}>
              <BNButton size="xs" w={80} onClick={onClose} disabled={isSellingPrivately}>
                Cancel
              </BNButton>
              <BNButton type="submit" size="xs" variant="filled" color="green" w={80} loading={isSellingPrivately}>
                Save
              </BNButton>
            </Flex>
          </Stack>
        </form>
      )}

      {ticketStockId === 'Pdf' && (
        <form
          onSubmit={pdfForm.onSubmit(async (values) => {
            await sellPrivately({
              data: {
                currencyCode: 'USD',
                totalAmount: values.totalPrice,
                customerId: '-1', // -1 implies Sell Privately and will be handled properly by the backend
                listingIds: [selectedListing.listingId],
                vendorProperties: {
                  pointOfSaleId: 'VictoryLiveOne',
                  keepCost: values.keepCost,
                } as BrokerNerdsBarkerCoreModelsSalesCreateSaleVendorPropertiesVictoryLive,
              },
            });
            onClose();
          })}
        >
          <Stack gap={6}>
            <Group gap="lg" pb={6}>
              <BNNumberInput
                size="lg"
                decimalScale={2}
                fixedDecimalScale
                selectOnFocus
                leftSection={
                  <Text size="lg" ml={-2}>
                    $
                  </Text>
                }
                {...pdfForm.getInputProps('totalPrice')}
                label="Total Price"
                labelProps={{ fz: 'xs' }}
                w={168}
              />
              <Box pt="lg">
                <Checkbox size="xs" {...pdfForm.getInputProps('keepCost')} label="Keep Cost" />
              </Box>
            </Group>
            <Divider orientation="horizontal" color="var(--colors-divider)" my="xs" mx={-16} />
            <Flex justify="flex-end" gap={12}>
              <BNButton size="xs" w={80} onClick={onClose} disabled={isSellingPrivately}>
                Cancel
              </BNButton>
              <BNButton type="submit" size="xs" variant="filled" color="green" w={80} loading={isSellingPrivately}>
                Save
              </BNButton>
            </Flex>
          </Stack>
        </form>
      )}
    </Modal>
  );
}
